import React from 'react';
import moment from 'moment';
import filter from 'lodash/filter';
import snakeCase from 'lodash/snakeCase';
import classnames from 'classnames/bind';
import { ResponsivePNG } from 'sports-react';

import StyledComponent from '~/components/Styled/Styled';
import LoadingSpinner from '~/components/LoadingSpinner/LoadingSpinner';

import ResultsMap from '~/constants/ResultsMap';

import getOr from '~/util/getOr';

import css from './MMAResultsMatchupCard.scss';

const cssContext = classnames.bind(css);

type Props = {
  event: any;
  eventRecords: any;
};

type State = {
  eventId: any;
};

class MMAResultsMatchupCard extends React.Component<Props, State> {
  static displayName = 'MMAResultsMatchupCard';

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.event.id === prevState.eventId) {
      return null;
    }
    return {
      eventId: nextProps.event.id,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      eventId: null,
    };
  }

  formatDescriptions(descriptionElement, event) {
    const propertyKeys = getOr(descriptionElement, 'properties', []);
    const format = getOr(descriptionElement, 'format', null);
    const seperator = getOr(descriptionElement, 'seperator', '');
    const label = getOr(descriptionElement, 'label', '');
    const currentEventStatus = getOr(this.props, 'event.event_status', '');
    const eventStatusAllowed = getOr(descriptionElement, 'event_status', []);
    let properties = propertyKeys.map(propertyKey => getOr(event, propertyKey, null));
    if (eventStatusAllowed.length && !eventStatusAllowed.includes(currentEventStatus)) {
      return null;
    }
    switch (descriptionElement.type) {
      case 'date':
        properties = properties.map(property => moment(property).format(format));
        break;
      case 'text':
        if (format) {
          let description = format;
          properties.forEach((property, i) => {
            description = description.replace(`%[${i}]`, property);
          });
          return `${label}${label && ': '}${description}`;
        }
        break;
      default:
        break;
    }
    return `${label}${label && ': '}${properties.join(seperator)}`;
  }

  renderEventCard(eventCardDescription, eventCard, event) {
    const leagueSlug = getOr(this.props, 'event.league.slug', '');
    const eventStatus = getOr(this.props, 'event.event_status', '');
    const playerKey = getOr(ResultsMap, `[${leagueSlug}].player.property`, {});
    const awayPlayer = getOr(event, `away_${playerKey}`, null);
    const homePlayer = getOr(event, `home_${playerKey}`, null);
    const winningPlayer = getOr(event, `winning_${playerKey}`, {});
    const recordKey = getOr(eventCard, 'playerRecord', null);
    const homeWon = homePlayer.id === winningPlayer.id;
    const awayWon = awayPlayer.id === winningPlayer.id;
    const awayPlayerNameClasses = cssContext(css.playerName, {
      playerLost: homeWon,
    });
    const homePlayerNameClasses = cssContext(css.playerName, {
      playerLost: awayWon,
    });
    const eventDescription = eventCardDescription[eventStatus] || eventCardDescription.default;
    const victoryType = getOr(event, 'victory.type', '');
    const victoryDescription = getOr(event, 'scorecard', '') || getOr(event, 'victory.method', '');
    return (
      <div className={css.eventCardContainer} key={event.id}>
        <div className={css.eventCardDescription}>
          {this.formatDescriptions(eventDescription, event)}
        </div>
        <div className={css.playerContainer}>
          <div className={css.playerFlag}>
            <ResponsivePNG width="24px" height="24px" src={getOr(awayPlayer, 'flag.tiny', null)} />
          </div>
          <div className={awayPlayerNameClasses}>
            {getOr(awayPlayer, 'first_initial_and_last_name', '')}
          </div>
          <div className={css.playerRecord}>{getOr(awayPlayer, recordKey, '')}</div>
          {awayWon && (
            <div className={css.victoryContainer}>
              <div className={css.victoryType}>
                {getOr(ResultsMap, `${leagueSlug}.victory.${snakeCase(victoryType)}`)}
              </div>
              &nbsp;
              {!!victoryDescription && (
                <div className={css.victoryDescription}>({victoryDescription})</div>
              )}
            </div>
          )}
        </div>
        <div className={css.playerContainer}>
          <div className={css.playerFlag}>
            <ResponsivePNG width="24px" height="24px" src={getOr(homePlayer, 'flag.tiny', null)} />
          </div>
          <div className={homePlayerNameClasses}>
            {getOr(homePlayer, 'first_initial_and_last_name', '')}
          </div>
          <div className={css.playerRecord}>{getOr(homePlayer, recordKey, '')}</div>
          {homeWon && (
            <div className={css.victoryContainer}>
              <div className={css.victoryType}>
                {getOr(ResultsMap, `${leagueSlug}.victory.${snakeCase(victoryType)}`)}
              </div>
              &nbsp;
              {!!victoryDescription && (
                <div className={css.victoryDescription}>({victoryDescription})</div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    const leagueSlug = getOr(this.props, 'event.league.slug', '');
    const matchupCards = getOr(ResultsMap, `[${leagueSlug}].matchupCards`, {});
    const eventCards = getOr(matchupCards, 'eventCards', []);
    const eventCardDescription = getOr(matchupCards, 'eventCardDescriptions', {});
    const eventRecords = getOr(this.props, 'eventRecords', {});
    const events = getOr(eventRecords, `[mma/${this.state.eventId}/fights]`, []);

    // eslint-disable-next-line no-prototype-builtins
    if (!eventRecords.hasOwnProperty(`${leagueSlug}/${this.state.eventId}/fights`)) {
      return (
        <div className={css.pregameNoticeContainer}>
          <div className={css.fetchingDataSpinner}>
            <LoadingSpinner SpinnerType="square_flip" />
          </div>
          <div className={css.preGameNoticeText1}>Please wait</div>
          <div className={css.preGameNoticeText2}>Fetching your data</div>
        </div>
      );
    }
    if (!events.length) {
      return (
        <div className={css.pregameNoticeContainer}>
          <div className={css.preGameNoticeText1}>Event hasn&apos;t started yet</div>
          <div className={css.preGameNoticeText2}>Please check back later for more details</div>
        </div>
      );
    }
    return (
      <div className={css.matchupRecordsContainer}>
        {eventCards.map(card => (
          <div key={card.label}>
            <div className={css.eventCardTitle}>{card.label}</div>
            {filter(events, [card.property, card.value]).map(event =>
              this.renderEventCard(eventCardDescription, card, event)
            )}
          </div>
        ))}
      </div>
    );
  }
}

export default StyledComponent(MMAResultsMatchupCard, [css]);
